import React, { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Cpu, MemoryStick, Check } from "lucide-react";
import { Kpi, Th, Td, Pill, SectionTitle, Loading, STATUS_STYLE, errMsg, usd, num } from "./shared";

const Gauge = ({ icon: Icon, title, unit, requested, used, recommended, overProvisioned, decreaseBy, headroomPct }) => {
  const max = Math.max(requested, 1);
  return (
    <div className="cs-card p-5" data-testid={`kube-gauge-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#00579e]" />
        <h3 className="cs-heading text-sm font-semibold">{title}</h3>
        <span className="ml-auto cs-mono text-xs text-muted-foreground">decrease by {num(decreaseBy, 2)} {unit}</span>
      </div>
      <div className="relative h-7 rounded-sm bg-[hsl(var(--cs-tint))] overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-[#cbd5e1]" style={{ width: `${(requested / max) * 100}%` }} />
        <div className="absolute inset-y-0 left-0 bg-[#00579e]/70" style={{ width: `${(recommended / max) * 100}%` }} />
        <div className="absolute inset-y-0 left-0 bg-[#15803d]" style={{ width: `${(used / max) * 100}%` }} />
      </div>
      <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
        {[["Requested", requested, "text-muted-foreground"], ["Used", used, "text-[#15803d]"],
          ["Recommended", recommended, "text-[#00579e]"], ["Over-provisioned", overProvisioned, "text-[#be185d]"]].map(([l, v, cls]) => (
          <div key={l}>
            <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{l}</div>
            <div className={`cs-mono font-semibold ${cls}`}>{num(v, 1)}</div>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground mt-2">
        Target sits <span className="cs-mono font-semibold text-foreground">{headroomPct}%</span> above observed average usage — percentile plus the stated safety margin, not slack.
      </div>
    </div>
  );
};

export const KubeRightSizing = ({ clusterId }) => {
  const [data, setData] = useState(null);
  const [percentile, setPercentile] = useState("class");
  const [namespace, setNamespace] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = () => {
    setData(null);
    api.kube.rightsizing(clusterId, percentile === "class" ? undefined : percentile, namespace)
      .then(setData).catch(console.error);
  };
  useEffect(load, [clusterId, percentile, namespace]);

  const openDetail = (id) => {
    api.kube.workload(clusterId, id, percentile === "class" ? undefined : percentile)
      .then(setDetail).catch(console.error);
  };

  const apply = async (mode) => {
    try {
      const cs = await api.kube.apply({ cluster_id: clusterId, workload_id: detail.workload.id, percentile: percentile === "class" ? undefined : percentile, mode });
      const line = `${cs.apply_path} · this cycle CPU ${cs.cpu_from_cores} → ${cs.cpu_to_cores} cores (target ${cs.cpu_target_cores} over ${cs.cycles_to_target} cycles) · ${usd(cs.monthly_savings_usd)}/mo at target`;
      if (cs.status === "pr_failed") {
        toast.error(`Could not open the pull request · ${cs.id}`, { description: cs.pull_request_error || cs.note });
      } else if (cs.status === "pr_open") {
        toast.success(`Pull request #${cs.pull_request?.number} opened · ${cs.id}`, { description: `${line} · the cluster changes when the PR merges` });
      } else if (cs.status === "applied_with_pr") {
        toast.success(`Applied and PR #${cs.pull_request?.number} opened · ${cs.id}`, { description: line });
      } else {
        toast.success(`${mode === "simulate" ? "Simulated" : "Applied"} · ${cs.id}`, { description: `${line} · ${cs.note}` });
      }
    } catch (e) {
      toast.error("Could not record the change", { description: errMsg(e) });
    }
  };

  if (!data) return <Loading label="Loading right-sizing…" testId="kube-rs-loading" />;
  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="kube-rightsizing">
      <div className="flex items-center gap-3 flex-wrap">
        <Kpi testId="kube-rs-potential" label="Potential monthly savings" value={usd(t.potential_monthly_savings_usd)}
          sub={`${t.workloads} workloads · ${t.containers} containers`} accent="text-[#15803d]" />
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Percentile</span>
          <Select value={percentile} onValueChange={setPercentile}>
            <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="kube-percentile-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="class" data-testid="kube-percentile-class">Per workload class (default)</SelectItem>
              {data.percentiles.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`kube-percentile-${p.id}`}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Namespace</span>
          <Select value={namespace} onValueChange={setNamespace}>
            <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="kube-namespace-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All namespaces</SelectItem>
              {data.namespaces.map((n) => (
                <SelectItem key={n} value={n} data-testid={`kube-ns-${n}`}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-[11px] text-muted-foreground max-w-[380px]">
          Defaults per class: {Object.entries(data.class_defaults).map(([k, v]) => `${k} ${v.toUpperCase()}`).join(" · ")} — each with its own margin.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Gauge icon={Cpu} title="vCPU" unit="cores" requested={t.cpu.requested} used={t.cpu.used}
          recommended={t.cpu.recommended} overProvisioned={t.cpu.over_provisioned}
          decreaseBy={t.cpu.decrease_by} headroomPct={t.cpu.headroom_above_usage_pct} />
        <Gauge icon={MemoryStick} title="Memory" unit="GiB" requested={t.memory.requested_gib} used={t.memory.used_gib}
          recommended={t.memory.recommended_gib} overProvisioned={t.memory.over_provisioned_gib}
          decreaseBy={t.memory.decrease_by_gib} headroomPct={t.memory.headroom_above_usage_pct} />
      </div>

      <div className="cs-card p-5" data-testid="kube-rs-table">
        <SectionTitle hint="Every row states the percentile used and the safety margin added on top of it.">Workload recommendations</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <Th>Workload</Th><Th>Namespace</Th><Th>Kind</Th><Th right>Replicas</Th><Th>HPA</Th>
                <Th right>vCPU now → rec</Th><Th right>Memory now → rec</Th><Th right>Margin</Th><Th right>Savings / mo</Th><Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {data.workloads.map((w) => (
                <tr key={w.id} className="border-t border-border hover:bg-[hsl(var(--cs-tint))] cursor-pointer transition-colors"
                  onClick={() => openDetail(w.id)} data-testid={`kube-rs-row-${w.workload}`}>
                  <Td className="font-medium">{w.workload}</Td>
                  <Td className="text-muted-foreground">{w.namespace}</Td>
                  <Td className="text-muted-foreground">{w.kind}</Td>
                  <Td right mono>{w.replicas}</Td>
                  <Td>{w.hpa ? <span className="text-[#6d28d9] font-medium">on</span> : <span className="text-muted-foreground">off</span>}</Td>
                  <Td right mono>{num(w.cpu_request_cores, 2)} → <span className="text-[#00579e] font-semibold">{num(w.cpu_recommended_cores, 2)}</span>
                    <span className="text-muted-foreground"> ({w.cpu_change_pct}%)</span></Td>
                  <Td right mono>{num(w.mem_request_mib / 1024, 1)} → <span className="text-[#00579e] font-semibold">{num(w.mem_recommended_mib / 1024, 1)}</span> GiB
                    <span className="text-muted-foreground"> ({w.mem_change_pct}%)</span></Td>
                  <Td right mono>{w.percentile.toUpperCase()} +{w.margin_pct}%
                    {w.cycles_to_target > 1 && <div className="text-[10px] text-muted-foreground">{w.cycles_to_target} cycles</div>}</Td>
                  <Td right mono className="font-semibold text-[#15803d]">{usd(w.monthly_savings_usd)}</Td>
                  <Td><Pill cls={STATUS_STYLE[w.status].cls}>{STATUS_STYLE[w.status].label}</Pill></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="kube-workload-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="cs-heading text-base">
                  {detail.workload.namespace}/{detail.workload.workload}
                  <span className="ml-2 text-xs font-normal text-muted-foreground">{detail.workload.kind} · {detail.workload.replicas} replicas · class {detail.workload.workload_class}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="cs-card p-4 border-l-4 border-l-[#00579e]" data-testid="kube-margin-explained">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Why this number</div>
                <p className="text-sm leading-relaxed">{detail.margin_explained}</p>
                <div className="cs-mono text-[11px] text-muted-foreground mt-1.5">
                  {detail.recommendation.percentile.toUpperCase()} usage {num(detail.recommendation.cpu_percentile_cores, 3)} cores × (1 + {detail.recommendation.margin_pct}%)
                  = {num(detail.recommendation.cpu_recommended_cores, 3)} cores requested
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Savings / mo" value={usd(detail.recommendation.monthly_savings_usd)} accent="text-[#15803d]" />
                <Kpi label="Data window" value={`${detail.evidence.data_window_days} days`} sub={`${num(detail.evidence.samples)} samples`} />
                <Kpi label="CPU throttling" value={`${detail.evidence.throttling_pct}%`} sub="threshold 5%" />
                <Kpi label="OOM kills (28d)" value={num(detail.evidence.oom_kills_28d)} sub="memory floor applies" />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr><Th>Evidence</Th><Th right>CPU p85</Th><Th right>CPU p95</Th><Th right>CPU p99</Th><Th right>Mem p95</Th><Th right>Mem 28d peak</Th></tr></thead>
                  <tbody>
                    <tr className="border-t border-border">
                      <Td className="text-muted-foreground">{detail.evidence.units}</Td>
                      <Td right mono>{num(detail.evidence.cpu_p85_cores, 3)}</Td>
                      <Td right mono>{num(detail.evidence.cpu_p95_cores, 3)}</Td>
                      <Td right mono>{num(detail.evidence.cpu_p99_cores, 3)}</Td>
                      <Td right mono>{num(detail.evidence.mem_p95_mib / 1024, 2)} GiB</Td>
                      <Td right mono>{num(detail.evidence.mem_28d_peak_mib / 1024, 2)} GiB</Td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {detail.containers.map((c) => (
                <div key={c.container} className="cs-card p-4" data-testid={`kube-container-${c.container}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs font-semibold">{c.container}</div>
                    <div className="cs-mono text-[11px] text-muted-foreground">
                      CPU {c.cpu_current_cores} → <span className="text-[#00579e] font-semibold">{c.cpu_recommended_cores}</span> (usage {c.cpu_usage_cores}) ·
                      Mem {c.mem_current_mib}Mi → <span className="text-[#00579e] font-semibold">{c.mem_recommended_mib}Mi</span> (usage {num(c.mem_usage_mib, 0)}Mi)
                    </div>
                  </div>
                  <pre className="mt-2 p-3 rounded-sm bg-[#0f172a] text-[#e2e8f0] cs-mono text-[11px] overflow-x-auto">{c.yaml}</pre>
                </div>
              ))}

              {detail.hpa_note && (
                <div className="text-[11px] text-[#6d28d9] bg-[#ede9fe] border border-[#ddd6fe] rounded-sm px-3 py-2">{detail.hpa_note}</div>
              )}
              <div className="text-[11px] text-muted-foreground" data-testid="kube-step-limits">
                Step limits: CPU max −{detail.step_limits.cpu_max_decrease_pct}% and memory max −{detail.step_limits.mem_max_decrease_pct}% per cycle.
                This cycle applies CPU <span className="cs-mono text-foreground">{detail.step_limits.cpu_first_step_cores}</span> cores and memory{" "}
                <span className="cs-mono text-foreground">{num(detail.step_limits.mem_first_step_mib / 1024, 1)}</span> GiB; the target is reached over{" "}
                <span className="cs-mono text-foreground">{detail.step_limits.cycles_to_target}</span> cycles.
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button data-testid="kube-simulate-btn" onClick={() => apply("simulate")}
                  className="px-3 py-1.5 rounded-sm border border-border text-xs hover:bg-[hsl(var(--cs-tint))] transition-colors">
                  Simulate
                </button>
                <button data-testid="kube-apply-btn" onClick={() => apply("apply")}
                  className="px-3 py-1.5 rounded-sm bg-[#00579e] text-white text-xs hover:bg-[#014a87] transition-colors inline-flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5" /> Approve &amp; apply
                </button>
                <span className="text-[11px] text-muted-foreground">
                  Apply path: {detail.cluster.in_place_resize ? "in-place resize, no restart" : "rolling restart"} · verified for 30 min with auto-rollback
                </span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KubeRightSizing;
